import { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { FormSubmissionResponse } from '@qdf/shared';

// Intake form (public)
import { Header } from './components/layout/Header.js';
import { Footer } from './components/layout/Footer.js';
import { SandboxBanner } from './components/layout/SandboxBanner.js';
import { IntakePage } from './components/pages/IntakePage.js';
import { ConfirmationPage } from './components/pages/ConfirmationPage.js';

// Portal (authenticated)
import { AuthProvider } from './contexts/AuthContext.js';
import { ProtectedRoute } from './components/portal/ProtectedRoute.js';
import { PortalLogin } from './components/portal/PortalLogin.js';
import { PortalLayout } from './components/portal/PortalLayout.js';
import { PortalDashboard } from './components/portal/PortalDashboard.js';
import { SubmissionsList } from './components/portal/SubmissionsList.js';
import { SubmissionDetail } from './components/portal/SubmissionDetail.js';
import { MonthlyReports } from './components/portal/MonthlyReports.js';
import { EmailTemplates } from './components/portal/EmailTemplates.js';

// New Western public form (served on its own domain — see brand.ts)
import { getBrand } from './brand.js';
import { NwApp } from './components/nw/NwApp.js';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false },
  },
});

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || 'PENDING_CLIENT_ID';

function IntakeApp() {
  const [submission, setSubmission] = useState<FormSubmissionResponse | null>(null);

  return (
    <div className="min-h-screen flex flex-col">
      <SandboxBanner />
      <Header />
      <main className="flex-1">
        {submission ? (
          <ConfirmationPage
            submission={submission}
            onStartNew={() => setSubmission(null)}
          />
        ) : (
          <IntakePage onSuccess={setSubmission} />
        )}
      </main>
      <Footer />
    </div>
  );
}

export default function App() {
  // New Western deployment serves ONLY the NW application form — no portal,
  // no Left Main intake. The Left Main tree below is untouched.
  if (getBrand() === 'newwestern') {
    return <NwApp />;
  }

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <AuthProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <Routes>
              {/* Public intake form */}
              <Route path="/" element={<IntakeApp />} />

              {/* Portal login */}
              <Route path="/portal/login" element={<PortalLogin />} />

              {/* Protected portal routes */}
              <Route
                path="/portal"
                element={
                  <ProtectedRoute>
                    <PortalLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<PortalDashboard />} />
                <Route path="submissions" element={<SubmissionsList />} />
                <Route path="submissions/:submissionId" element={<SubmissionDetail />} />
                <Route path="reports" element={<MonthlyReports />} />
                <Route path="email-templates" element={<EmailTemplates />} />
              </Route>

              {/* Catch-all */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </QueryClientProvider>
      </AuthProvider>
    </GoogleOAuthProvider>
  );
}
